import React, { useState } from 'react';
import { MapPin, CheckCircle2, AlertTriangle, ExternalLink, Search, Globe, Camera } from 'lucide-react';
import VerifyAddressDialog from './VerifyAddressDialog';
import PropertyMap from './PropertyMap';
import { resolveAddressDisplay } from './resolveAddressDisplay';

/**
 * PropertyLocationSection
 *
 * Visual property context surface on the Customer Profile. Renders a
 * read-only satellite view of the customer's address (via the existing
 * Maps JS loader — no Maps Embed API involved) and lets the operator
 * verify or correct the location against Google Places.
 *
 * Two data sources, kept conceptually separate:
 *   - `lead.address` / `lead.addressDisplay` → CRM/service address,
 *     operator-controlled. Resolved via resolveAddressDisplay() so all
 *     legacy shapes (string, {line1,city,state,zip}, {streetNumber,...})
 *     render correctly.
 *   - `lead.location` → verified Google metadata
 *     { verified, verifiedAt, verifiedBy, placeId, formattedAddress,
 *       lat, lng, addressComponents, source }
 *     Source enum: manual | google_verified | facebook_import |
 *                  csv_import | api_import | website_form
 *
 * Map rendering:
 *   - Verified (lat/lng on `location`)         → <PropertyMap> satellite view + marker
 *   - Unverified + non-empty address           → empty-state CTA prompting Verify
 *   - No address at all                        → empty-state, prompts for address entry
 *
 * Click "Open in Maps" → Google Maps in a new tab. This is intentionally
 * a property *viewer* — no drawing tools, no measurements.
 */
const PropertyLocationSection = ({ client }) => {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [mapMode, setMapMode] = useState('satellite');           // 'satellite' | 'street'
  const [streetAvailable, setStreetAvailable] = useState(true);  // probed by PropertyMap

  const address = resolveAddressDisplay(client);
  const location = client?.location || null;
  const hasLatLng = typeof location?.lat === 'number' && typeof location?.lng === 'number';
  const isVerified = location?.verified === true && (hasLatLng || !!location?.placeId);
  const hasAddress = !!address.trim();

  // Open-in-Google-Maps deep link. Prefer the verified place_id when
  // available — it disambiguates identical street addresses across cities.
  const openInMapsHref = (() => {
    if (isVerified) {
      const q = encodeURIComponent(location.formattedAddress || address || '');
      const placeId = location.placeId ? `&query_place_id=${encodeURIComponent(location.placeId)}` : '';
      return `https://www.google.com/maps/search/?api=1&query=${q}${placeId}`;
    }
    if (hasAddress) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  })();

  const sourceLabel = (() => {
    if (!location?.source) return null;
    const map = {
      manual:           'Operator-entered',
      google_verified:  'Verified via Google',
      facebook_import:  'Facebook Lead Ad',
      csv_import:       'CSV import',
      api_import:       'API import',
      website_form:     'Website form',
    };
    return map[location.source] || location.source;
  })();

  return (
    <>
      <VerifyAddressDialog
        client={client}
        isOpen={verifyOpen}
        onClose={() => setVerifyOpen(false)}
      />

      <section
        className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3"
        data-testid="property-location-section"
      >
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">Property Location</h2>
            {isVerified ? (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-0.5"
                data-testid="property-location-verified-pill"
                title={location.verifiedAt ? `Verified ${new Date(location.verifiedAt?.toMillis?.() || location.verifiedAt).toLocaleDateString()}` : 'Verified'}
              >
                <CheckCircle2 className="h-3 w-3" />
                Verified
              </span>
            ) : hasAddress ? (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-full px-2.5 py-0.5"
                data-testid="property-location-unverified-pill"
              >
                <AlertTriangle className="h-3 w-3" />
                Unverified
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5"
                data-testid="property-location-no-address-pill"
              >
                No address
              </span>
            )}
          </div>
          {hasAddress && (
            <button
              type="button"
              onClick={() => setVerifyOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="property-location-verify-btn"
            >
              <Search className="h-3.5 w-3.5" />
              {isVerified ? 'Re-verify' : 'Verify with Google'}
            </button>
          )}
        </header>

        {/* Address line — always reflects the operator-controlled service
            address. The verified Google formatted_address is shown as a
            secondary annotation only when it differs. */}
        {hasAddress ? (
          <div className="text-xs text-gray-600 leading-snug">
            <p className="font-medium text-gray-800">{address}</p>
            {isVerified && location.formattedAddress && location.formattedAddress !== address && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid="property-location-google-address">
                Google: {location.formattedAddress}
              </p>
            )}
            {sourceLabel && (
              <p
                className="text-[10px] text-gray-400 mt-1 italic"
                data-testid="property-location-source"
              >
                Source: {sourceLabel}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-500 italic">
            No service address on file. Use the Edit Client menu to add one.
          </p>
        )}

        {/* Map / verify-prompt area */}
        {isVerified && hasLatLng ? (
          <div className="space-y-2">
            {/* Segmented toggle — Satellite (default) | Street View */}
            <div
              className="inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5 text-xs font-medium"
              role="tablist"
              aria-label="Map view"
              data-testid="property-map-mode-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={mapMode === 'satellite'}
                onClick={() => setMapMode('satellite')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                  mapMode === 'satellite'
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="property-map-mode-satellite"
              >
                <Globe className="h-3.5 w-3.5" />
                Satellite
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mapMode === 'street'}
                onClick={() => setMapMode('street')}
                disabled={!streetAvailable && mapMode !== 'street'}
                title={!streetAvailable && mapMode !== 'street'
                  ? 'Street View imagery is not available at this location'
                  : 'Switch to Street View'}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                  mapMode === 'street'
                    ? 'bg-emerald-600 text-white'
                    : !streetAvailable
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="property-map-mode-street"
              >
                <Camera className="h-3.5 w-3.5" />
                Street View
              </button>
            </div>

            <div className="relative">
              <PropertyMap
                key={mapMode}
                lat={location.lat}
                lng={location.lng}
                title={location.formattedAddress || address}
                mode={mapMode}
                onStreetViewAvailability={setStreetAvailable}
                testId="property-location-map"
              />
              {openInMapsHref && (
                <a
                  href={openInMapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-white/95 backdrop-blur-sm px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-white shadow-sm transition-colors z-20"
                  data-testid="property-location-open-in-maps"
                >
                  Open in Maps
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        ) : hasAddress ? (
          <div
            className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-center"
            data-testid="property-location-verify-prompt"
          >
            <MapPin className="h-6 w-6 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-600 leading-snug">
              Verify this address with Google to see the property satellite view.
            </p>
            <button
              type="button"
              onClick={() => setVerifyOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
              data-testid="property-location-verify-prompt-btn"
            >
              <Search className="h-3.5 w-3.5" />
              Verify with Google
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
};

export default PropertyLocationSection;
